import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import CowAvatar from '../components/CowAvatar';
import CowLoader from '../components/CowLoader';
import Toast from '../components/Toast';

export default function NotificationsPage() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });
  const clearToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifs(res.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFriend = async (notif, action) => {
    setActionLoading(notif.id + action);
    try {
      if (action === 'accept') await api.post(`/friends/accept/${notif.id}`);
      else await api.delete(`/friends/${notif.id}`);
      showToast(action === 'accept' ? 'Solicitud aceptada' : 'Solicitud rechazada');
      load();
    } finally { setActionLoading(null); }
  };

  const handlePayment = async (notif, action) => {
    setActionLoading(notif.id + action);
    try {
      if (action === 'confirm') await api.post(`/personal-debts/${notif.id}/confirm`);
      else await api.post(`/personal-debts/${notif.id}/deny`);
      showToast(action === 'confirm' ? 'Pago confirmado' : 'Pago rechazado');
      load();
    } finally { setActionLoading(null); }
  };

  const handleInvitation = async (notif, action) => {
    setActionLoading(notif.id + action);
    try {
      if (action === 'accept') {
        const res = await api.post(`/vaquita-invitations/${notif.id}/accept`);
        showToast('Te uniste a la vaquita');
        load();
        if (res.data?.inviteCode) navigate(`/vaquita/code/${res.data.inviteCode}`);
      } else {
        await api.post(`/vaquita-invitations/${notif.id}/reject`);
        showToast('Invitación rechazada');
        load();
      }
    } finally { setActionLoading(null); }
  };

  if (loading) return <CowLoader text="Cargando notificaciones..." />;

  return (
    <div className="notifs-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
      <h2 className="notifs-title">🔔 Notificaciones</h2>

      {notifs.length === 0 ? (
        <div className="notifs-empty">
          <span className="notifs-empty-icon">🎉</span>
          <p>Todo al día, no hay notificaciones pendientes.</p>
        </div>
      ) : (
        <div className="notifs-list">
          {notifs.map((n, i) => (
            <div key={i} className={`notif-card notif-${n.type.toLowerCase()}`}>
              <div className="notif-card-row">
                <div className="notif-card-avatar">
                  {n.type === 'FRIEND_REQUEST' && <CowAvatar seed={n.profilePicture || n.userId} size={46} />}
                  {n.type === 'VAQUITA_INVITATION' && <CowAvatar seed={n.inviterPicture || n.inviterId} size={46} />}
                  {n.type === 'DEBT' && <CowAvatar seed={n.lenderPicture || n.lenderId} size={46} />}
                </div>
                <div className="notif-card-body">
                  <span className="notif-type-pill">
                    {n.type === 'FRIEND_REQUEST' && '🤝 Solicitud de amistad'}
                    {n.type === 'VAQUITA_INVITATION' && '🐮 Invitación a vaquita'}
                    {n.type === 'DEBT' && '💸 Deuda pendiente'}
                    {n.type === 'PAYMENT_CONFIRMATION' && '💰 Confirmar pago recibido'}
                  </span>
                  <p className="notif-card-text">
                    {n.type === 'FRIEND_REQUEST' && (
                      <><strong>{n.userName}</strong> quiere agregarte como amigo</>
                    )}
                    {n.type === 'VAQUITA_INVITATION' && (
                      <><strong>{n.inviterName}</strong> te invitó a unirte a <strong>{n.vaquitaName}</strong></>
                    )}
                    {n.type === 'DEBT' && (
                      <>Le debes <strong>${n.amount?.toLocaleString('es-CL')}</strong> a <strong>{n.lenderName}</strong> en <strong>{n.vaquitaName}</strong></>
                    )}
                    {n.type === 'PAYMENT_CONFIRMATION' && (
                      <><strong>{n.borrowerName}</strong> dice que te pagó <strong>${n.amount?.toLocaleString('es-CL')}</strong> de <strong>{n.vaquitaName}</strong></>
                    )}
                  </p>
                  {n.type === 'PAYMENT_CONFIRMATION' && n.paymentProofUrl && (
                    <a href={`/api${n.paymentProofUrl}`} target="_blank" rel="noreferrer" className="notif-proof-link">📎 Ver comprobante</a>
                  )}
                </div>
              </div>

              <div className="notif-card-actions">
                {n.type === 'FRIEND_REQUEST' && (
                  <>
                    <button className="nca accept" disabled={!!actionLoading} onClick={() => handleFriend(n, 'accept')}>Aceptar</button>
                    <button className="nca reject" disabled={!!actionLoading} onClick={() => handleFriend(n, 'reject')}>Rechazar</button>
                    <button className="nca view" onClick={() => navigate(`/user/${n.userId}`)}>Ver perfil</button>
                  </>
                )}
                {n.type === 'VAQUITA_INVITATION' && (
                  <>
                    <button className="nca accept" disabled={!!actionLoading} onClick={() => handleInvitation(n, 'accept')}>Unirme</button>
                    <button className="nca reject" disabled={!!actionLoading} onClick={() => handleInvitation(n, 'reject')}>Rechazar</button>
                  </>
                )}
                {n.type === 'DEBT' && (
                  <button className="nca view" onClick={() => navigate(`/vaquita/code/${n.inviteCode}`)}>Ver vaquita</button>
                )}
                {n.type === 'PAYMENT_CONFIRMATION' && (
                  <>
                    <button className="nca accept" disabled={!!actionLoading} onClick={() => handlePayment(n, 'confirm')}>Confirmar recibo</button>
                    <button className="nca reject" disabled={!!actionLoading} onClick={() => handlePayment(n, 'deny')}>Rechazar</button>
                    <button className="nca view" onClick={() => navigate(`/vaquita/code/${n.inviteCode}`)}>Ver vaquita</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .notifs-page {
          max-width: 560px;
          margin: 0 auto;
          padding: 1.5rem 1rem 80px;
        }

        .notifs-title {
          color: var(--text-primary);
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 1.25rem;
        }

        .notifs-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--text-muted);
          font-size: 0.95rem;
        }

        .notifs-empty-icon { font-size: 2.5rem; display: block; margin-bottom: 0.75rem; }

        .notifs-list { display: flex; flex-direction: column; gap: 0.75rem; }

        .notif-card {
          background: var(--bg-secondary);
          border-radius: 14px;
          padding: 1rem 1.1rem;
          box-shadow: 0 2px 10px var(--shadow-color);
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          border-left: 4px solid transparent;
        }

        .notif-friend_request { border-left-color: var(--accent-color); }
        .notif-vaquita_invitation { border-left-color: #f59e0b; }
        .notif-debt { border-left-color: var(--danger-color); }

        .notif-card-row {
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
        }

        .notif-card-avatar { flex-shrink: 0; padding-top: 4px; }

        .notif-card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .notif-type-pill {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-muted);
        }

        .notif-card-text {
          font-size: 0.92rem;
          color: var(--text-secondary);
          line-height: 1.4;
          margin: 0;
        }

        .notif-card-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          padding-left: calc(46px + 0.9rem);
        }

        .nca {
          padding: 0.4rem 1rem;
          border-radius: 8px;
          border: none;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s;
        }

        .nca:disabled { opacity: 0.6; cursor: not-allowed; }
        .nca:hover:not(:disabled) { opacity: 0.85; }
        .notif-proof-link { font-size: 0.8rem; color: var(--accent-color); padding-left: calc(46px + 0.9rem); }
        .notif-payment_confirmation { border-left-color: #22c55e; }
        .nca.accept { background: var(--accent-color); color: white; }
        .nca.reject { background: transparent; color: var(--danger-color); border: 1.5px solid var(--danger-color); }
        .nca.view { background: var(--bg-primary); color: var(--text-secondary); border: 1px solid var(--border-color); }
      `}</style>
    </div>
  );
}
